import { useEffect, useMemo, useState } from "react";
import { App } from "antd";
import { adminFetch, errorMessage } from "./adminApi";
import type { SupabaseUserOption } from "../types/filterCopy";

/**
 * Użytkownicy Supabase do selectów i list panelu (`GET supabase/users`). `activeOnly`
 * zawęża po stronie backendu do osób z aktywną subskrypcją; zmiana przeładowuje listę.
 *
 * `keepIds` — aktualnie wybrani użytkownicy. Zostają na liście, nawet gdy filtr ich odsiewa,
 * żeby włączenie filtra nie gubiło wyboru (select pokazałby wtedy goły UUID, a strony
 * szukające e-maila wybranej osoby straciłyby go).
 *
 * `setUsers` pozwala stronie poprawić listę lokalnie (np. po usunięciu konta).
 */
export function useSupabaseUsers(activeOnly = false, keepIds: ReadonlyArray<string | undefined> = []) {
  const { notification } = App.useApp();
  const [users, setUsers] = useState<SupabaseUserOption[]>([]);
  const [known, setKnown] = useState<ReadonlyMap<string, SupabaseUserOption>>(() => new Map());
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const requestKey = activeOnly ? "active" : "all";

  useEffect(() => {
    let cancelled = false;
    adminFetch<{ data: SupabaseUserOption[] }>(`supabase/users${activeOnly ? "?active_only=true" : ""}`)
      .then((response) => {
        if (cancelled) return;
        const data = response.data ?? [];
        setUsers(data);
        setKnown((previous) => {
          const next = new Map(previous);
          for (const user of data) next.set(user.id, user);
          return next;
        });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          notification.error({ message: "Nie udało się pobrać użytkowników", description: errorMessage(error) });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadedKey(activeOnly ? "active" : "all");
      });
    return () => {
      cancelled = true;
    };
  }, [activeOnly, notification]);

  // Klucz zamiast tablicy w zależnościach — strony przekazują nową tablicę przy każdym renderze.
  const keepKey = keepIds.filter(Boolean).join(",");
  const visibleUsers = useMemo(() => {
    if (!keepKey) return users;
    const present = new Set(users.map((user) => user.id));
    const kept = keepKey
      .split(",")
      .filter((id) => !present.has(id))
      .flatMap((id) => {
        const user = known.get(id);
        return user ? [user] : [];
      });
    return kept.length ? [...users, ...kept] : users;
  }, [keepKey, known, users]);

  // Ładowanie wyprowadzone z klucza żądania — bez synchronicznego setState w efekcie.
  return { users: visibleUsers, setUsers, loading: loadedKey !== requestKey };
}
